import { settings, storage } from './storage.js';
import { t } from './i18n.js';
import { escapeHtml } from './utils.js';

/* Ayudas en pantalla: una burbuja corta, una sola vez por pantalla, que señala lo que un espectador nuevo
   no suele encontrar solo (buscar, filtrar, guardar en Mi lista, cambiar de espectador). No roba el foco ni
   bloquea nada: se cierra al tocar, al pulsar una tecla, al cambiar de pantalla o a los pocos segundos. */

const KEY = 'coachSeen';

/** Qué pantalla enseña qué, y a qué elemento señala. */
export const HINTS = {
    home: { key: 'viewers', anchor: '.home-topbar-avatar, .appnav-avatar', icon: 'account_circle' },
    search: { key: 'search', anchor: '[data-search]', icon: 'search' },
    movies: { key: 'filters', anchor: '.filters .filter-btn', icon: 'filter_list' },
    series: { key: 'filters', anchor: '.filters .filter-btn', icon: 'filter_list' },
    'movie-detail': { key: 'fav', anchor: '[data-fav]', icon: 'favorite' },
    'series-detail': { key: 'fav', anchor: '[data-fav]', icon: 'favorite' },
    // Solo en la tele: con el mando no se alcanza el «+» de las tarjetas; se añade manteniendo OK.
    live: { key: 'tvHold', anchor: '.card-channel, .chitem', icon: 'touch_app', tvOnly: true }
};

export function hintFor(pageName) {
    return HINTS[pageName] || null;
}

function seenMap() {
    const m = storage.get(KEY, {});
    return m && typeof m === 'object' ? m : {};
}

let bubble = null;
let timer = 0;
let offs = [];

export const coach = {
    enabled() {
        return settings.get('coach') !== false;
    },
    seen(key) {
        return !!seenMap()[key];
    },
    mark(key) {
        const m = seenMap();
        m[key] = Date.now();
        storage.set(KEY, m);
    },
    /** «Ver las ayudas otra vez». */
    reset() {
        storage.remove(KEY);
    },
    hide() {
        clearTimeout(timer);
        offs.forEach((f) => f());
        offs = [];
        if (bubble) { bubble.remove(); bubble = null; }
    },
    /** ¿Se puede enseñar esa ayuda ahora? (la pantalla actual la decide el que llama) */
    shouldShow(pageName, { isTV = false } = {}) {
        const h = hintFor(pageName);
        return !!h && !(h.tvOnly && !isTV) && this.enabled() && !this.seen(h.key);
    },
    /** Muestra la ayuda de la pantalla si toca y su elemento se ve. → true si se mostró. */
    show(pageName, root, { isTV = false } = {}) {
        if (!this.shouldShow(pageName, { isTV })) return false;
        const h = hintFor(pageName);
        const anchor = Array.from((root || document).querySelectorAll(h.anchor)).find((el) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
        });
        if (!anchor) return false;
        this.hide();
        const r = anchor.getBoundingClientRect();
        const vw = document.documentElement.clientWidth, vh = window.innerHeight;
        const w = Math.min(isTV ? 420 : 300, vw - 24);
        const left = Math.max(12, Math.min(r.left + r.width / 2 - w / 2, vw - w - 12));
        const below = r.bottom + 150 < vh - 70;
        const el = document.createElement('div');
        el.className = 'coach ' + (below ? 'is-below' : 'is-above');
        el.setAttribute('role', 'status');
        el.style.width = w + 'px';
        el.style.left = left + 'px';
        el.style.setProperty('--coach-x', Math.max(18, Math.min(r.left + r.width / 2 - left, w - 18)) + 'px');
        if (below) el.style.top = (r.bottom + 10) + 'px'; else el.style.bottom = (vh - r.top + 10) + 'px';
        el.innerHTML = `<span class="mi coach-icon">${h.icon}</span><div class="coach-body"><div class="coach-text">${escapeHtml(t('coach.' + h.key))}</div><button type="button" class="coach-ok" ${isTV ? 'data-nav-skip tabindex="-1"' : ''}>${escapeHtml(t('coach.ok'))}</button></div>`;
        document.body.appendChild(el);
        bubble = el;
        this.mark(h.key);
        const close = () => this.hide();
        el.querySelector('.coach-ok').addEventListener('click', close);
        const onDown = (e) => { if (!el.contains(e.target)) close(); };
        const onKey = () => close(); // sin consumir la tecla: el mando sigue funcionando como siempre
        document.addEventListener('pointerdown', onDown, true);
        document.addEventListener('keydown', onKey, true);
        offs.push(() => document.removeEventListener('pointerdown', onDown, true), () => document.removeEventListener('keydown', onKey, true));
        timer = setTimeout(close, isTV ? 14000 : 11000);
        return true;
    }
};
